import { PrintJob } from '@/types'
import { Card,CardHeader,CardTitle,CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { X,AlertCircle,Printer as PrinterIcon,HardDrive,RotateCw,Clock,Layout } from 'lucide-react'
import { ImageWithSkeleton } from '@/components/ui/image-with-skeleton'
import { JobStatusBadge } from '@/features/devices/job-status-badge'
import { JobTypeBadge } from '@/features/devices/job-type-badge'
import { JobActions } from '@/features/devices/job-actions'
import { formatDistanceToNow } from 'date-fns'
import { formatInStoreTz } from '@/lib/time'

interface JobDetailCardProps {
  job: PrintJob
  printerName?: string
  agentName?: string
  onClose: () => void
}

export function JobDetailCard({
  job,
  printerName,
  agentName,
  onClose,
}: JobDetailCardProps) {
  return (
    <Card className="squircle w-full md:w-[320px] lg:w-[360px] sticky top-0 max-h-[calc(100vh-6.5rem)] animate-in slide-in-from-right-10 fade-in duration-300 shadow-lg pb-4 flex flex-col shrink-0">
      <CardHeader className="pb-0 shrink-0 flex flex-row items-center justify-between">
        <div className="flex flex-col gap-0.5 overflow-hidden">
          <div className="flex items-center gap-2">
            <CardTitle className="truncate text-lg" title={job.title || `Job #${job.id.slice(-5)}`}>
              Job #{job.id.slice(-5)}
            </CardTitle>
            <JobStatusBadge status={job.status} dotOnly />
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0 h-8 w-8">
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>

      <CardContent className="flex-1 overflow-auto pt-0 pb-0 flex flex-col gap-5">
        {job.preview && (
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-2">
              Preview
            </span>
            <ImageWithSkeleton
              src={job.preview}
              alt="Label preview"
              wrapperClassName="w-full rounded-md border shadow-sm overflow-hidden bg-muted/50"
              className="w-full h-auto object-contain"
            />
          </div>
        )}

        <div className="space-y-4">
          <div className="bg-muted/30 border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between h-7">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Layout className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Type</span>
              </div>
              <JobTypeBadge jobType={job.jobType} />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <PrinterIcon className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Printer</span>
              </div>
              <span className="text-xs font-medium truncate max-w-[150px]" title={printerName || job.printer?.name}>
                {printerName || job.printer?.name || '—'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <HardDrive className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Agent</span>
              </div>
              <span className="text-xs font-medium truncate max-w-[150px]" title={agentName || job.printAgent?.name}>
                {agentName || job.printAgent?.name || '—'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <RotateCw className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Attempts</span>
              </div>
              <span className="text-xs font-medium">
                {job.retries} of {job.maxRetries}
              </span>
            </div>
          </div>
        </div>

        {job.errorMessage && (
          <div className="mt-2 p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-700 text-sm flex flex-col gap-1">
            <div className="flex items-center font-semibold">
              <AlertCircle className="w-4 h-4 mr-2" />
              Error Details
            </div>
            <p className="break-words font-mono text-xs">{job.errorMessage}</p>
          </div>
        )}

        <JobActions job={job} />

        <div className="pt-4 mt-auto border-t border-border/50 grid grid-cols-2 gap-4 text-xs text-muted-foreground">
          <div>
            <span className="font-medium text-foreground block mb-1">Created</span>
            {formatInStoreTz(job.createdAt, 'MMM d, yyyy')}
          </div>
          <div>
            <span className="font-medium text-foreground block mb-1">Printed</span>
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {job.printedAt
                ? formatDistanceToNow(new Date(job.printedAt), { addSuffix: true })
                : 'Pending'}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
